/**
 * Ort und Zeit aus einem Video lesen.
 *
 * **Warum das hier steht, obwohl es einmal abgelehnt war.** Beim Bau der
 * Videos an Punkten hieß es: kein „Aus Video", ein Video trage seinen Ort im
 * Browser nur selten lesbar. Das war zu pauschal. Ein Video vom iPhone trägt
 * ihn sehr wohl — als `©xyz` im `udta`-Atom, in ISO 6709, genau dort, wo auch
 * das Aufnahmedatum steht. Aus dem Betrieb: *„Standort aus Video übernehmen,
 * wenn vorhanden."*
 *
 * **Gelesen wird die Datei nicht ganz.** Ein Video ist Hunderte Megabyte; die
 * interessanten Angaben stehen in einem Verwaltungsatom von wenigen Kilobyte.
 * Deshalb läuft dieser Leser die Atomkette am Dateianfang entlang und liest
 * **nur die Kopfzeilen** (acht Bytes je Atom, über `Blob.slice`), bis er `moov`
 * findet — erst das wird ganz geholt. Auf einem Telefon über Mobilfunk ist das
 * der Unterschied zwischen „geht sofort" und „lädt eine halbe Minute".
 *
 * **Was nicht gelesen werden kann, ist kein Fehler.** Ein WebM aus einem
 * Browser, ein weitergeleitetes Video ohne Verwaltungsdaten, ein Format, das
 * hier niemand kennt: dann kommt `befund` zurück und sonst nichts, und der
 * Punkt öffnet wie bisher am Gerätestandort. Ein Reisebericht soll nicht daran
 * scheitern, dass eine Nebenangabe fehlte.
 */

import type { Aufnahme, Befund } from './exif'
import type { Uhr } from './ortszeit'
import { istVerlauf, wanduhrZuUtc } from './zonen'

/** Vier Zeichen als Zahl — so stehen die Atomnamen in der Datei. */
function typ(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  )
}

/**
 * Die Atomkette am Dateianfang entlanglaufen und `moov` holen.
 *
 * Ein MP4 besteht aus Atomen: vier Bytes Länge, vier Bytes Name, dann der
 * Inhalt. `moov` steht bei Apple-Aufnahmen mal vorn, mal hinter den Bilddaten
 * (`mdat`) — deshalb wird die Kette wirklich abgelaufen und nicht geraten.
 *
 * **Die Länge 1 ist kein Sonderfall zum Weglassen.** Sie heißt: die echte
 * Länge steht als 64-Bit-Zahl dahinter. Bei `mdat` eines langen 4K-Videos ist
 * das der Normalfall, und wer sie übergeht, verliest sich um Gigabyte und
 * findet `moov` nie.
 */
async function holeMoov(datei: Blob): Promise<Uint8Array | null> {
  let offset = 0
  // Eine Obergrenze gegen kaputte Dateien: ohne sie liefe die Schleife auf
  // einem verstümmelten Video bis ans Dateiende, Atom für Atom.
  for (let schritte = 0; schritte < 64 && offset + 8 <= datei.size; schritte++) {
    const kopf = new DataView(await datei.slice(offset, offset + 16).arrayBuffer())
    if (kopf.byteLength < 8) return null

    let laenge = kopf.getUint32(0)
    const name = typ(kopf, 4)
    let inhaltAb = offset + 8

    if (laenge === 1) {
      if (kopf.byteLength < 16) return null
      // 64 Bit, und `getBigUint64` gibt es nicht überall gleich gutmütig --
      // die obere Hälfte zählt nur, wenn die Datei wirklich über 4 GB geht.
      laenge = kopf.getUint32(8) * 2 ** 32 + kopf.getUint32(12)
      inhaltAb = offset + 16
    } else if (laenge === 0) {
      // 0 heißt „bis zum Dateiende". Dann ist es das letzte Atom.
      laenge = datei.size - offset
    }

    if (laenge < 8) return null
    if (name === 'moov') {
      // Ein `moov` von mehr als 32 MB ist kein Verwaltungsatom mehr, sondern
      // ein Missverständnis -- lieber nichts als das Telefon lahmlegen.
      const ende = Math.min(offset + laenge, inhaltAb + 32 * 1024 * 1024)
      return new Uint8Array(await datei.slice(inhaltAb, ende).arrayBuffer())
    }
    offset += laenge
  }
  return null
}

/** Vier Zeichen an einer Stelle — leer, wenn dort nichts Druckbares steht. */
function typDavon(bytes: Uint8Array, offset: number): string {
  if (offset + 4 > bytes.byteLength) return ''
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const name = typ(view, offset)
  return /^[\x20-\x7e]{4}$/.test(name) ? name : ''
}

/**
 * Der Rumpf eines `meta`-Atoms — mit oder ohne Vorspann.
 *
 * **Hier gehen MP4 und QuickTime auseinander.** In ISO-BMFF (MP4) ist `meta`
 * eine „FullBox": vier Bytes Version und Flags stehen vor den Kindern. In
 * einem QuickTime-Film (`.mov`, und das ist es, was ein iPhone aufnimmt) ist
 * es eine gewöhnliche Box ohne diesen Vorspann. Wer sich für eine Variante
 * entscheidet, zerschneidet bei der anderen das erste Kind.
 *
 * Entschieden wird deshalb an der Datei selbst: steht bei Offset 4 schon ein
 * lesbarer Atomname, gibt es keinen Vorspann.
 */
function metaRumpf(meta: Uint8Array): Uint8Array {
  return typDavon(meta, 4) === '' ? meta.subarray(4) : meta
}

/**
 * In einem Atomrumpf nach einem Namen suchen — auch verschachtelt.
 *
 * `©xyz` liegt in `moov/udta`, bei manchen Kameras eine Ebene tiefer in
 * `moov/meta/…`. Statt jeden Pfad einzeln zu kennen, wird der Baum abgestiegen
 * und nach dem Namen gesucht. Verwaltungsatome sind klein; das kostet nichts.
 */
function sucheAtom(bytes: Uint8Array, gesucht: string, tiefe = 0): Uint8Array | null {
  if (tiefe > 6) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  let offset = 0
  while (offset + 8 <= bytes.byteLength) {
    const laenge = view.getUint32(offset)
    const name = typ(view, offset + 4)
    if (laenge < 8 || offset + laenge > bytes.byteLength) break

    const inhalt = bytes.subarray(offset + 8, offset + laenge)
    if (name === gesucht) return inhalt

    // Nur in die Behälter absteigen, die welche sind. Blind in jedes Atom zu
    // greifen liest Bilddaten als Atomnamen und findet Zufallstreffer.
    if (name === 'udta' || name === 'meta' || name === 'ilst' || name === 'trak') {
      const rumpf = name === 'meta' ? metaRumpf(inhalt) : inhalt
      const treffer = sucheAtom(rumpf, gesucht, tiefe + 1)
      if (treffer !== null) return treffer
    }
    offset += laenge
  }
  return null
}

/**
 * Der Text in einem `©…`-Atom.
 *
 * Der Rumpf beginnt mit zwei Bytes Länge und zwei Bytes Sprachcode; danach
 * kommt der Text. Manche Schreiber verpacken ihn zusätzlich in ein `data`-Atom
 * mit acht Bytes Vorspann — beides kommt vor, beides wird bedient.
 */
function textVon(atom: Uint8Array | null): string | null {
  if (atom === null || atom.byteLength < 4) return null

  const data = sucheAtom(atom, 'data')
  const roh = data !== null && data.byteLength > 8 ? data.subarray(8) : atom.subarray(4)
  const text = new TextDecoder().decode(roh).trim()
  return text === '' ? null : text
}

/**
 * **Alle** Atome dieses Namens — nicht nur das erste.
 *
 * Der Unterschied ist kein Feinschliff, er war der Fehler. Ein
 * QuickTime-Film vom iPhone trägt **mehrere** `meta`-Atome: eines je Spur mit
 * den Kameradaten (`camera.lens_model`, `apple-maker-note…`) und eines auf
 * Filmebene mit Ort und Aufnahmezeit. Die Spuren stehen im `moov` **vor** dem
 * Film-`meta` — wer das erste nimmt, bekommt Brennweite und Blende und hört
 * dort auf.
 *
 * Aus dem Betrieb, an der Diagnosezeile abgelesen: `moov 34 kB ·
 * camera.lens_model, camera.focal_length.35mm_equivalent, …` — fünf Schlüssel,
 * und kein einziger davon der Ort.
 */
function alleAtome(bytes: Uint8Array, gesucht: string, tiefe = 0): Uint8Array[] {
  if (tiefe > 6) return []
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const treffer: Uint8Array[] = []

  let offset = 0
  while (offset + 8 <= bytes.byteLength) {
    const laenge = view.getUint32(offset)
    const name = typ(view, offset + 4)
    if (laenge < 8 || offset + laenge > bytes.byteLength) break

    const inhalt = bytes.subarray(offset + 8, offset + laenge)
    if (name === gesucht) treffer.push(inhalt)
    else if (name === 'udta' || name === 'meta' || name === 'ilst' || name === 'trak') {
      const rumpf = name === 'meta' ? metaRumpf(inhalt) : inhalt
      treffer.push(...alleAtome(rumpf, gesucht, tiefe + 1))
    }
    offset += laenge
  }
  return treffer
}

/**
 * Apples Metadaten: `moov/meta` mit `keys` und `ilst`.
 *
 * **Der Weg, den ein iPhone heute wirklich nimmt.** `©xyz` im `udta` ist die
 * alte Ablage; sie kommt weiter vor, aber ein aktuelles iOS schreibt Ort und
 * Aufnahmezeit als benannte Schlüssel:
 *
 * ```
 * moov/meta/keys   com.apple.quicktime.location.ISO6709
 *                  com.apple.quicktime.creationdate
 * moov/meta/ilst   [1] → data → "+43.1234+006.3456+012.000/"
 *                  [2] → data → "2026-05-22T15:22:00+0200"
 * ```
 *
 * **Die Verbindung ist die Nummer, nicht der Name.** Im `ilst` heißen die
 * Atome nicht wie ihr Schlüssel, sondern tragen dessen **1-basierten Index**
 * als vier Rohbytes. Wer im `ilst` nach `com.apple…` sucht, findet nie etwas —
 * genau daran ist der erste Anlauf gescheitert.
 *
 * @return die Werte je Schlüsselname
 */
function appleSchluessel(moov: Uint8Array): Map<string, string> {
  const werte = new Map<string, string>()
  // **Jedes** `meta`, nicht das erste: die Kameradaten der Videospur stehen
  // vorn, Ort und Zeit weiter hinten auf Filmebene.
  for (const meta of alleAtome(moov, 'meta')) {
    ausEinemMeta(metaRumpf(meta), werte)
  }
  return werte
}

/** Ein einzelnes `meta` auswerten und in die Sammlung schreiben. */
function ausEinemMeta(rumpf: Uint8Array, werte: Map<string, string>): void {
  const keys = sucheAtom(rumpf, 'keys')
  const ilst = sucheAtom(rumpf, 'ilst')
  if (keys === null || ilst === null) return

  // --- Die Namen, der Reihe nach ---
  const namen: string[] = []
  const kview = new DataView(keys.buffer, keys.byteOffset, keys.byteLength)
  // Vier Bytes Version/Flags, vier Bytes Anzahl, dann die Einträge.
  let ko = 8
  while (ko + 8 <= keys.byteLength) {
    const laenge = kview.getUint32(ko)
    if (laenge < 8 || ko + laenge > keys.byteLength) break
    // Der Namensraum steht dahinter (`mdta`), der Name ist der Rest.
    namen.push(new TextDecoder().decode(keys.subarray(ko + 8, ko + laenge)))
    ko += laenge
  }

  // --- Und die Werte, adressiert über die Nummer ---
  const iview = new DataView(ilst.buffer, ilst.byteOffset, ilst.byteLength)
  let io = 0
  while (io + 8 <= ilst.byteLength) {
    const laenge = iview.getUint32(io)
    if (laenge < 8 || io + laenge > ilst.byteLength) break

    const nummer = iview.getUint32(io + 4)
    const name = namen[nummer - 1]
    if (name !== undefined) {
      const data = sucheAtom(ilst.subarray(io + 8, io + laenge), 'data')
      // Acht Bytes Vorspann: vier für den Datentyp, vier für die Sprache.
      if (data !== null && data.byteLength > 8) {
        const text = new TextDecoder().decode(data.subarray(8)).trim()
        if (text !== '') werte.set(name, text)
      }
    }
    io += laenge
  }
}

/**
 * ISO 6709 zerlegen: `+44.8151-066.9498+010.000/`.
 *
 * Vorzeichen sind Pflicht, und genau daran wird erkannt, wo die Breite aufhört
 * und die Länge anfängt. Die Höhe hinten interessiert nicht.
 */
function ausIso6709(text: string): { lat: number; lon: number } | null {
  const treffer = /^([+-]\d+(?:\.\d+)?)([+-]\d+(?:\.\d+)?)/.exec(text)
  if (treffer === null) return null

  const lat = Number(treffer[1])
  const lon = Number(treffer[2])
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null
  // 0/0 liegt im Golf von Guinea und ist in der Praxis immer ein Empfänger
  // ohne Fix -- dieselbe Regel wie beim Bild und auf dem Server.
  if (lat === 0 && lon === 0) return null

  return { lat, lon }
}

/**
 * Die Aufnahmezeit aus `©day`, samt Zonenangabe wenn sie dabeisteht.
 *
 * Apple schreibt `2023-09-28T14:30:00-0400`. Der Versatz ist das Wertvolle:
 * ohne ihn stünde am Punkt die Uhr des Geräts, das gerade zusieht, statt der
 * Uhr des Ortes, an dem gefilmt wurde — derselbe Punkt wie beim Foto und
 * `OffsetTimeOriginal`.
 */
function ausZeittext(
  text: string,
  uhr: Uhr = null,
): { at: string; tz_offset_min: number | null; wanduhr: string | null } | null {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?/
    .exec(text)
  if (treffer === null) return null

  const [, jahr, monat, tag, stunde, minute, sekunde, zone] = treffer

  let versatz: number | null = null
  if (zone === 'Z') {
    versatz = 0
  } else if (zone !== undefined) {
    const zeichen = zone.startsWith('-') ? -1 : 1
    const ziffern = zone.slice(1).replace(':', '')
    versatz = zeichen * (Number(ziffern.slice(0, 2)) * 60 + Number(ziffern.slice(2)))
  }

  // Without a zone the time is a wall clock of an unknown zone. Until 1.86 it
  // was read as UTC and only the offset was taken from the trip -- the moment
  // stayed off by exactly that offset. Since 1.87.0 it is read in the trip's
  // zone course at that moment, like a photo without `OffsetTimeOriginal`,
  // and the wall clock goes along so the server can do the same.
  const alsUtc = Date.UTC(
    Number(jahr), Number(monat) - 1, Number(tag),
    Number(stunde), Number(minute), Number(sekunde),
  )
  if (!Number.isFinite(alsUtc)) return null

  if (versatz === null) {
    const wanduhr = `${jahr}-${monat}-${tag}T${stunde}:${minute}:${sekunde}`
    const aufDemVerlauf = istVerlauf(uhr) && uhr.length > 0 ? wanduhrZuUtc(uhr, wanduhr) : undefined
    const schiebe = typeof uhr === 'number' ? uhr : 0
    const at = aufDemVerlauf ?? new Date(alsUtc - schiebe * 60_000).toISOString()
    return { at, tz_offset_min: null, wanduhr }
  }
  return { at: new Date(alsUtc - versatz * 60_000).toISOString(), tz_offset_min: versatz, wanduhr: null }
}

/**
 * Die Marke aus dem `ftyp` — `qt  `, `isom`, `mp42` …
 *
 * `null` heißt: kein Behälter, den dieser Leser kennt. Die Marke selbst steht
 * nur in der Diagnose; gelesen wird jede, denn die Atomstruktur ist dieselbe.
 */
async function ftypMarke(datei: Blob): Promise<string | null> {
  if (datei.size < 12) return null
  const kopf = new DataView(await datei.slice(0, 12).arrayBuffer())
  // `ftyp` steht am Anfang jedes MP4 und jedes QuickTime-Films.
  if (typ(kopf, 4) !== 'ftyp') return null
  return typ(kopf, 8).trim() || 'ohne Marke'
}

/**
 * Was ein Video über sich preisgibt — **samt der Auskunft, wie weit der Leser
 * kam**.
 *
 * Die Diagnose ist kein Beiwerk: Dateityp, Größe, Behältermarke, ob `moov`
 * gefunden wurde und welche Schlüssel darin standen. Aus dem Betrieb kam
 * „keine Geodaten im Video, aber in der Foto App sieht man sie" — und daran
 * war ohne diese Zeile nicht zu unterscheiden, ob das Format unbekannt ist
 * oder ob iOS beim Übergeben eine umgewandelte Datei ohne Verwaltungsdaten
 * geliefert hat. Beim DNG hat genau diese Ununterscheidbarkeit zwei Runden
 * gekostet.
 */
export type Videoaufnahme = Aufnahme & { diagnose: string }

/**
 * Was dieses Video über Ort und Zeit sagt.
 *
 * Dieselbe Form wie beim Bild (`aufnahmeAus`), damit „Aus Foto/Video" beide
 * Wege gleich behandeln kann: ein Video mit Ortsangabe zählt dort genauso wie
 * ein Foto mit Ortsangabe, und die Reihenfolge der Auswahl entscheidet.
 */
export async function aufnahmeAusVideo(
  datei: Blob,
  reiseVersatz: Uhr = null,
): Promise<Videoaufnahme> {
  const teile: string[] = []
  if (datei instanceof File && datei.type !== '') teile.push(datei.type)
  teile.push(`${(datei.size / 1024 / 1024).toFixed(1)} MB`)

  const leer = (befund: Befund): Videoaufnahme => ({
    at: null,
    lat: null,
    lon: null,
    tz_offset_min: null,
    befund,
    diagnose: teile.join(' · '),
  })

  try {
    const marke = await ftypMarke(datei)
    if (marke === null) return leer('kein-bild')
    teile.push(`ftyp ${marke}`)

    const moov = await holeMoov(datei)
    if (moov === null) {
      teile.push('kein moov')
      return leer('ohne-exif')
    }
    teile.push(`moov ${(moov.byteLength / 1024).toFixed(0)} kB`)

    // **Beide Ablagen, in dieser Reihenfolge.** `©xyz` ist billig zu prüfen
    // und deckt ältere Dateien und fremde Kameras; Apples Schlüssel sind der
    // Weg, den ein aktuelles iPhone geht. Es kommt vor, dass nur eines von
    // beidem dasteht -- deshalb wird nicht abgebrochen, sondern ergänzt.
    let ort = ausIso6709(textVon(sucheAtom(moov, '©xyz')) ?? '')
    let zeit = ausZeittext(textVon(sucheAtom(moov, '©day')) ?? '', reiseVersatz)

    const apple = appleSchluessel(moov)
    ort = ort ?? ausIso6709(apple.get('com.apple.quicktime.location.ISO6709') ?? '')
    zeit = zeit ?? ausZeittext(apple.get('com.apple.quicktime.creationdate') ?? '', reiseVersatz)

    // **Was drinstand, gehört in die Diagnose** -- nicht nur, was fehlte. Aus
    // dem Betrieb kam „keine Geodaten im Video, aber in der Foto App sieht man
    // sie", und daran war ohne diese Zeile nicht zu erkennen, ob das Format
    // unbekannt oder die Datei leer war.
    // Wie viele Behälter es überhaupt gab -- **daran hing der erste Fehler**:
    // ausgewertet wurde nur das erste `meta`, und das ist bei einem
    // QuickTime-Film das der Videospur mit Brennweite und Blende.
    const metas = alleAtome(moov, 'meta').length
    if (metas !== 1) teile.push(`${metas}× meta`)

    const gefunden = [
      ...['©xyz', '©day'].filter((n) => sucheAtom(moov, n) !== null),
      ...[...apple.keys()].map((k) => k.replace('com.apple.quicktime.', '')),
    ]
    teile.push(
      gefunden.length === 0
        ? 'keine Angaben'
        : gefunden.length > 10
          ? `${gefunden.slice(0, 10).join(', ')} +${gefunden.length - 10}`
          : gefunden.join(', '),
    )

    if (ort === null && zeit === null) return leer('ohne-exif')

    return {
      at: zeit?.at ?? null,
      lat: ort?.lat ?? null,
      lon: ort?.lon ?? null,
      // Without a zone in the video the wall clock goes along, like a photo's.
      tz_offset_min: zeit?.tz_offset_min ?? null,
      wanduhr: zeit?.wanduhr ?? null,
      befund: 'gelesen',
      diagnose: teile.join(' · '),
    }
  } catch (fehler) {
    teile.push(fehler instanceof Error ? fehler.message : 'Fehler beim Lesen')
    return leer('unlesbar')
  }
}
